import React, { useState } from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { toast } from 'react-toastify';
import { Button } from 'components/Button';
import CloseIcon from 'assets/svg/close-icon.svg';
import { Tooltip } from '@material-ui/core';
import InspectionPhotoService from 'services/ApiService/InspectionPhotoService/InspectionPhotoService';
import DocumentService from 'services/DocumentService/documentService';
import { useMutateInspection } from 'hooks/Inspections/InspectionsHooks';
import { DocumentCategoryEnum } from 'pages/SchedulerList/components/Inspection/Inspection.types';
import { IUploadRegulationDialogProps } from './UploadRegulationDialog.types';
import {
  ContainerButton,
  Description,
  DialogBase,
  DragAndDropArea,
  ErrorMessage,
  FileItem,
  FileList,
  FileTypeButton,
  FileTypeSelector,
  RemoveButton,
  Title,
} from './UploadRegulationDialog.styles';

type FileType = 'photo' | 'document';

const UploadRegulationDialog = ({ dialogVisibility, inspection, handleClose }: IUploadRegulationDialogProps) => {
  const [images, setImages] = useState<ImageListType>([]);
  const [loading, setLoading] = useState(false);
  const [fileType, setFileType] = useState<FileType>('photo');

  const { invalidateInspectionParallel } = useMutateInspection();

  const isButtonSubmitDisabled = images.length === 0;

  const onChange = (imageList: ImageListType) => {
    setImages(imageList);
  };

  const handleCloseModal = (onImageRemoveAll: () => void) => {
    if (!loading) {
      onImageRemoveAll();
      setFileType('photo');
      handleClose();
    }
  };

  const getTotalRegulationPhotos = (): number => {
    return inspection.photos.filter((photo) => photo.origin === 'UPLOAD_KITE_REGULATION').length;
  };

  const getTotalRegulationDocuments = (): number => {
    return inspection.files?.filter((doc) => doc.origin === 'UPLOAD_KITE_REGULATION').length || 0;
  };

  const getMaxStepFromRegulationPhotos = (): number => {
    const photos = inspection.photos.filter((photo) => photo.origin === 'UPLOAD_KITE_REGULATION');
    const maxStep = photos.reduce((acc, photo) => {
      if (photo.step && photo.step > acc) return photo.step;
      return acc;
    }, 0);
    return maxStep;
  };

  const handleSubmitPhotos = async (onImageRemoveAll: () => void) => {
    try {
      setLoading(true);
      const totalRegulationPhotos = getTotalRegulationPhotos();
      const maxStepFromPhotos = getMaxStepFromRegulationPhotos();

      const photosForUpload = images.map((image, index) => {
        const actualPhotoIndex = index + 1;
        const uploadIndex = actualPhotoIndex + totalRegulationPhotos;

        return {
          name: `regulation upload ${uploadIndex}`,
          description: `regulation upload ${uploadIndex}`,
          step: maxStepFromPhotos + actualPhotoIndex,
          origin: 'UPLOAD_KITE_REGULATION',
          base64: image.dataURL,
          file: image.file,
        };
      });

      const photosResolved = await InspectionPhotoService.uploadAllPhotosToAwsS3(inspection.id, photosForUpload);
      const photosUploaded = InspectionPhotoService.filterAllPhotosUploadedToAwsS3(photosResolved);
      await InspectionPhotoService.savePhotos(inspection.id, photosUploaded);

      onImageRemoveAll();
      setLoading(false);
      invalidateInspectionParallel(inspection.id);
      handleClose();
      toast.success('Fotos de regulação enviadas com sucesso!');
    } catch (error) {
      setLoading(false);
      toast.error('Falha ao enviar fotos de regulação.');
    }
  };

  const handleSubmitDocuments = async (onImageRemoveAll: () => void) => {
    try {
      setLoading(true);
      const totalRegulationDocuments = getTotalRegulationDocuments();

      await DocumentService.uploadDocuments(
        inspection.id,
        images,
        totalRegulationDocuments,
        DocumentCategoryEnum.REPORT,
        'UPLOAD_KITE_REGULATION'
      );

      onImageRemoveAll();
      setLoading(false);
      invalidateInspectionParallel(inspection.id);
      handleClose();
      toast.success('Documentos de regulação enviados com sucesso!');
    } catch (error) {
      setLoading(false);
      toast.error('Falha ao enviar documentos de regulação.');
    }
  };

  const handleSubmit = (onImageRemoveAll: () => void) => {
    if (fileType === 'photo') {
      handleSubmitPhotos(onImageRemoveAll);
    } else {
      handleSubmitDocuments(onImageRemoveAll);
    }
  };

  const getAcceptTypes = () => {
    return fileType === 'photo' ? ['jpg', 'jpeg', 'png'] : ['pdf', 'xls', 'xlsx', 'doc', 'docx'];
  };

  const getDragDropText = () => {
    return fileType === 'photo'
      ? 'Clique ou arraste fotos aqui (JPG, PNG)'
      : 'Clique ou arraste documentos aqui (PDF, Excel, Word)';
  };

  const getErrorMessage = () => {
    return fileType === 'photo'
      ? '* Tipo de arquivo não suportado para fotos. Use apenas JPG ou PNG'
      : '* Tipo de arquivo não suportado para documentos. Use PDF, Excel ou Word';
  };

  const getDocumentIcon = (fileName: string) => {
    const typeDocument = fileName.split('.')[1];
    return DocumentService.documentIcon(typeDocument);
  };

  return (
    <ImageUploading
      multiple
      value={images}
      onChange={onChange}
      acceptType={getAcceptTypes()}
      allowNonImageType={fileType === 'document'}
    >
      {({ imageList, onImageUpload, onImageRemoveAll, onImageRemove, isDragging, dragProps, errors }) => (
        <DialogBase
          open={dialogVisibility}
          onClose={(e: React.MouseEvent<HTMLElement>) => handleCloseModal(onImageRemoveAll)}
          disableEnforceFocus
        >
          <Title>Enviar Arquivos de Regulação</Title>

          <Description>Selecione o tipo de arquivo e faça o upload de fotos ou documentos para regulação</Description>

          <FileTypeSelector>
            <FileTypeButton
              isActive={fileType === 'photo'}
              onClick={() => {
                setFileType('photo');
                onImageRemoveAll();
              }}
            >
              Fotos
            </FileTypeButton>
            <FileTypeButton
              isActive={fileType === 'document'}
              onClick={() => {
                setFileType('document');
                onImageRemoveAll();
              }}
            >
              Documentos
            </FileTypeButton>
          </FileTypeSelector>

          <DragAndDropArea
            isDragging={isDragging}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              onImageUpload();
            }}
            {...dragProps}
          >
            <p>{getDragDropText()}</p>
          </DragAndDropArea>

          {errors?.acceptType && <ErrorMessage>{getErrorMessage()}</ErrorMessage>}

          {imageList.length > 0 && (
            <FileList>
              {imageList.map((image, index) => (
                <FileItem key={`${image.dataURL || image.file?.name},${index}`} isDocument={fileType === 'document'}>
                  {fileType === 'photo' && image.dataURL ? (
                    <img src={image.dataURL} alt={image.file?.name} />
                  ) : (
                    <>
                      <img src={getDocumentIcon(image.file?.name || '')} alt="Documento" />
                      <span>{image.file?.name}</span>
                    </>
                  )}
                  <Tooltip title="Remover arquivo" placement="top">
                    <RemoveButton
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        onImageRemove(index);
                      }}
                    >
                      <img src={CloseIcon} alt="Remover" />
                    </RemoveButton>
                  </Tooltip>
                </FileItem>
              ))}
            </FileList>
          )}

          <ContainerButton>
            <Button
              text="Cancelar"
              variant="text"
              onClick={(e: React.MouseEvent<HTMLElement>) => handleCloseModal(onImageRemoveAll)}
              disabled={loading}
            />
            <Button
              text="Enviar"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLElement>) => handleSubmit(onImageRemoveAll)}
              disabled={isButtonSubmitDisabled || loading}
              loading={loading}
            />
          </ContainerButton>
        </DialogBase>
      )}
    </ImageUploading>
  );
};

export default UploadRegulationDialog;
